import { SelfContainedCommand, CommandExecutionContext } from '../../../types/commands';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../../types/standardToolResults';
import { localToolExecutor } from './localToolExecutor';
import { createFileLogger } from '../../../utils/logger';

const log = createFileLogger('ToolCommands');

export const toolCommands: SelfContainedCommand[] = [
  {
    id: 'tool_execute',
    description: `Execute any command-line tool on user's local machine with their consent.

🎯 WORKFLOW:
1. Input files are auto-downloaded to /input/ with original filenames
2. Your bash script runs in a clean temp workspace
3. Output files in /output/ are auto-uploaded and become new entities
4. User sees and approves the command before it runs

📁 ENVIRONMENT:
• Working directory: temp workspace root
• Input files: /input/ directory (preserves original filenames, including spaces)
• Output location: /output/ directory (all files here become entities)
• Use wildcards (/input/*) or quotes ("/input/file.png") for paths

✅ BEST PRACTICES FOR SIMPLE TASKS:
• Start simple - use wildcards first: magick /input/* /output/result.webp
• Output must go to /output/ directory (not /input/, not working dir!)
• Don't debug with ls/file commands - just try the conversion directly
• If it works, output files automatically become entities
• User gets clear success/error messages with full stdout/stderr

🛠️ AVAILABLE TOOLS (examples - user may have more/less):
- Images: magick, convert, cwebp, optipng, pngquant, ffmpeg
- Video: ffmpeg, ffprobe, HandBrakeCLI
- Audio: sox, ffmpeg, lame
- Documents: pandoc, libreoffice, gs (ghostscript)
- Archives: zip, tar, 7z
- Text: sed, awk, jq
- Python/Node: Run scripts if user has libraries installed

📝 FULL BASH SCRIPTING SUPPORTED:
- Pipes: cmd1 | cmd2
- Loops: for f in /input/*; do ...; done
- Conditionals: if [ -f file ]; then ...; fi
- Variables: name="value"`,
    
    autonomyLevel: 'confirm',
    visualTreatment: 'demonstrative',
    
    schema: {
      parameters: [
        {
          name: 'script',
          type: 'string',
          description: `Bash script to execute. Keep it SIMPLE - wildcards work great!

🎯 KEY RULES:
1. Input files are in /input/ with original names (may have spaces!)
2. Output MUST go to /output/ - files here auto-upload as entities
3. Use wildcards (/input/*) when possible - simpler and handles spaces
4. Working dir is temp workspace root (not /input/ or /output/)

✅ RECOMMENDED PATTERNS:
• Simple wildcard: magick /input/* /output/result.webp
• With options: ffmpeg -i /input/* -c:v libwebp /output/result.webp
• Multiple outputs: for f in /input/*; do magick "$f" "/output/$(basename "$f" .png).webp"; done

❌ AVOID:
• Don't debug with ls/file unless needed - just try the conversion
• Don't output to /input/ or current dir - use /output/ only
• Don't manually escape spaces in filenames - use wildcards or quotes

Examples:
1. Image resize: magick /input/* -resize 800x /output/resized.webp
2. Video convert: ffmpeg -i /input/* -c:v libx264 /output/video.mp4
3. PDF compress: gs -sDEVICE=pdfwrite -dCompatibilityLevel=1.4 -dPDFSETTINGS=/ebook -sOutputFile=/output/compressed.pdf /input/*`,
          required: true
        },
        {
          name: 'inputEntityIds',
          type: 'array',
          description: 'Entity IDs of input files. Files will be available in /input/ with their original filenames.',
          required: true
        },
        {
          name: 'outputName',
          type: 'string',
          description: 'Optional: Name for the primary output file. If multiple outputs, only first is renamed.',
          required: false
        },
        {
          name: 'requiresTools',
          type: 'array',
          description: 'Optional: List of tools this script needs (e.g., ["ffmpeg", "python3"]). Used to check availability before requesting consent.',
          required: false
        }
      ]
    },
    
    examples: {
      good: [
        {
          input: {
            script: 'magick /input/* /output/converted.webp',
            inputEntityIds: ['entity-abc-123'],
            requiresTools: ['magick']
          },
          expectedOutput: createSuccessResult('Image converted to WebP', {
            newEntityId: 'entity-xyz-789',
            stdout: '',
            executionLocation: 'local'
          }),
          description: 'SIMPLE: Convert image to WebP with wildcard (handles spaces automatically)'
        },
        {
          input: {
            script: `
for file in /input/*.jpg; do
  filename=\$(basename "$file" .jpg)
  convert "$file" -resize 50% "/output/\${filename}_half.jpg"
done
            `,
            inputEntityIds: ['img-1', 'img-2', 'img-3'],
            requiresTools: ['convert']
          },
          expectedOutput: createSuccessResult('Batch resized 3 images', {
            newEntityIds: ['out-1', 'out-2', 'out-3'],
            executionLocation: 'local'
          }),
          description: 'Batch resize multiple images with loop'
        },
        {
          input: {
            script: 'ffmpeg -i /input/video.mp4 -vn -acodec libmp3lame -q:a 2 /output/audio.mp3',
            inputEntityIds: ['video-456'],
            outputName: 'extracted_audio.mp3',
            requiresTools: ['ffmpeg']
          },
          expectedOutput: createSuccessResult('Audio extracted', {
            newEntityId: 'audio-789',
            executionLocation: 'local'
          }),
          description: 'Extract audio from video'
        }
      ],
      bad: [
        {
          input: {
            script: '',
            inputEntityIds: ['entity-123']
          },
          error: 'Script cannot be empty',
          why: 'No command provided',
          fix: 'Provide a valid bash script'
        },
        {
          input: {
            script: 'convert input.png output.webp',
            inputEntityIds: []
          },
          error: 'At least one input entity required',
          why: 'No input entities provided',
          fix: 'Provide entity IDs for input files'
        },
        {
          input: {
            script: 'ls -la /input/ && file /input/* && magick /input/* /output/result.webp',
            inputEntityIds: ['entity-123']
          },
          error: 'Unnecessary complexity',
          why: 'Debug commands (ls, file) waste user time approving multiple retries',
          fix: 'Just run: magick /input/* /output/result.webp (system shows full stdout/stderr on error)'
        },
        {
          input: {
            script: 'magick /input/file.png /input/converted.webp',
            inputEntityIds: ['entity-123']
          },
          error: 'Wrong output directory',
          why: 'Output to /input/ will not create entities - must use /output/',
          fix: 'Change to: magick /input/* /output/converted.webp'
        }
      ]
    },
    
    performance: {
      expectedDuration: 5000,
      complexity: 'medium'
    },
    
    handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
      const startTime = Date.now();
      
      try {
        // Validate inputs
        if (!params.script || params.script.trim() === '') {
          return createErrorResult(
            'Script cannot be empty',
            'Please provide a valid bash script',
            'validation'
          );
        }
        
        if (!params.inputEntityIds || params.inputEntityIds.length === 0) {
          return createErrorResult(
            'At least one input entity required',
            'Please provide entity IDs for input files',
            'validation'
          );
        }
        
        log.log('[TOOL_EXECUTE] Starting local execution:', {
          scriptLength: params.script.length,
          inputCount: params.inputEntityIds.length,
          requiresTools: params.requiresTools,
          script: params.script,
          spaceId: context.spaceId,
          userId: context.userId
        });
        
        // Execute locally
        const result = await localToolExecutor.execute({
          script: params.script,
          inputEntityIds: params.inputEntityIds,
          outputName: params.outputName,
          requiresTools: params.requiresTools,
          spaceId: context.spaceId,
          userId: context.userId
        });
        
        log.log('[TOOL_EXECUTE] Execution result:', {
          success: result.success,
          error: result.error,
          exitCode: result.exitCode,
          stdoutLength: result.stdout?.length || 0,
          stderrLength: result.stderr?.length || 0,
          hasNewEntityId: !!result.newEntityId,
          newEntityIds: result.newEntityIds
        });
        
        if (!result.success) {
          log.error('[TOOL_EXECUTE] ❌ Execution failed:', {
            error: result.error,
            stderr: result.stderr,
            stdout: result.stdout,
            exitCode: result.exitCode
          });
          
          return createErrorResult(
            result.error || 'Execution failed',
            `${result.stderr || result.error || 'Command failed to execute'}\n\nstdout: ${result.stdout}\nexit code: ${result.exitCode}`,
            'execution',
            {
              data: {
                script: params.script,           // Include script for renderer
                exitCode: result.exitCode,
                stdout: result.stdout,
                stderr: result.stderr,
                requiresTools: params.requiresTools
              }
            }
          );
        }
        
        // ✅ INSTANT UI UPDATE: Fetch and add new entities to EntityStore
        if (result.newEntityIds && result.newEntityIds.length > 0) {
          log.log('[TOOL_EXECUTE] Fetching new entities to add to store:', {
            entityIds: result.newEntityIds
          });
          
          try {
            const { entityService } = await import('../../../services/entityService');
            const { default: useEntityStore } = await import('../../../stores/entityStore');
            const entityStore = useEntityStore.getState();
            
            // Fetch all new entities and add them to the store
            for (const entityId of result.newEntityIds) {
              try {
                const entity = await entityService.get(entityId);
                
                // Add to store using the _applyLocalUpsert helper
                entityStore._applyLocalUpsert(entity, {
                  log: true,
                  source: 'local',
                  syncCRDT: true
                });
                
                log.log('[TOOL_EXECUTE] ✅ Added new entity to store:', {
                  id: entity._id,
                  name: entity.name,
                  type: entity.entityType
                });
              } catch (fetchError) {
                log.warn('[TOOL_EXECUTE] Failed to fetch new entity:', {
                  entityId,
                  error: fetchError
                });
              }
            }
          } catch (importError) {
            log.error('[TOOL_EXECUTE] Failed to import entity services:', importError);
          }
        }
        
        log.log('[TOOL_EXECUTE] ✅ Execution successful:', {
          newEntityId: result.newEntityId,
          entityCount: result.newEntityIds?.length || 1,
          executionTime: Date.now() - startTime
        });
        
        return createSuccessResult(
          'Command executed successfully on local machine',
          {
            script: params.script,           // Include script for renderer
            exitCode: result.exitCode,        // Include exit code
            newEntityId: result.newEntityId,
            newEntityIds: result.newEntityIds,
            stdout: result.stdout,
            stderr: result.stderr,
            requiresTools: params.requiresTools  // Include required tools
          }
        );
        
      } catch (error: any) {
        log.error('[TOOL_EXECUTE] ❌ Execution failed:', error);
        
        return createErrorResult(
          'Tool execution failed',
          error.message || 'Unknown error',
          'execution'
        );
      }
    }
  }
];

